import { useCallback, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import { Badge } from 'src/@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/@/components/ui/tooltip';
import PerguntasFlowCanvas from './perguntas/PerguntasFlowCanvas';
import PerguntasGlobaisCard from './perguntas/PerguntasGlobaisCard';
import { LIMITE_ATIVAS, LIMITE_QUALIF } from './perguntas/catalog';

function ModoCard({ active, onClick, icon, title, description }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex items-start gap-3 text-left p-4 rounded-xl border-2 transition-all ${
                active
                    ? 'border-[#7C3AED] bg-violet-50/50 shadow-sm shadow-[#7C3AED]/10'
                    : 'border-gray-200 bg-white hover:border-violet-200'
            }`}
        >
            <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    active ? 'bg-[#7C3AED] text-white' : 'bg-gray-50 text-gray-500'
                }`}
            >
                <Iconify icon={icon} width={20} />
            </div>
            <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${active ? 'text-[#6d28d9]' : 'text-gray-900'}`}>{title}</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>
            </div>
            <div
                className={`w-4 h-4 rounded-full border-2 shrink-0 mt-1 ${
                    active ? 'border-[#7C3AED] bg-[#7C3AED]' : 'border-gray-300 bg-white'
                }`}
            >
                {active && (
                    <div className="w-full h-full rounded-full flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>
                )}
            </div>
        </button>
    );
}

function InfoBadge({ variant, count, limit, label, tooltip, atingiu }) {
    const colorMap = {
        violet: atingiu
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-violet-50 text-violet-700 border-violet-200',
        blue: atingiu
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-blue-50 text-blue-700 border-blue-200',
    };
    const dotMap = {
        violet: atingiu ? 'bg-amber-500' : 'bg-violet-500',
        blue: atingiu ? 'bg-amber-500' : 'bg-blue-500',
    };

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex cursor-help outline-none">
                    <Badge
                        variant="outline"
                        className={`text-xs font-medium flex items-center gap-1.5 ${colorMap[variant]}`}
                    >
                        <span className={`w-1.5 h-1.5 rounded-full ${dotMap[variant]}`} />
                        {count} / {limit} {label}
                        <Iconify icon="eva:info-outline" width={12} className="opacity-60" />
                    </Badge>
                </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="end" className="max-w-xs text-xs leading-relaxed">
                {tooltip}
            </TooltipContent>
        </Tooltip>
    );
}

export default function PerguntasEssenciaisSection({ aiAgent, changeModuleConfig }) {
    const perguntasEssenciais = aiAgent?.config?.module?.realstate?.perguntasEssenciais || {};
    const workflow = perguntasEssenciais.workflow || { nodes: [], edges: [] };
    const modo = perguntasEssenciais.modo === 'fixa' ? 'fixa' : 'por_tipo';
    const perguntasFixas = useMemo(
        () => (Array.isArray(perguntasEssenciais.perguntasFixas) ? perguntasEssenciais.perguntasFixas : []),
        [perguntasEssenciais.perguntasFixas]
    );

    const nodePerguntas = useMemo(() => {
        if (modo === 'fixa') return perguntasFixas;
        return (workflow.nodes || []).flatMap((n) => n.data?.perguntas || []);
    }, [modo, perguntasFixas, workflow.nodes]);

    const totalAtivas = nodePerguntas.filter((p) => p.ativa).length;
    const totalQualif = nodePerguntas.filter((p) => p.qualificatoria).length;

    const limiteAtivasAtingido = totalAtivas >= LIMITE_ATIVAS;
    const limiteQualifAtingido = totalQualif >= LIMITE_QUALIF;

    const getEssencialDisabled = useCallback(
        (state) => !state?.ativa && limiteAtivasAtingido,
        [limiteAtivasAtingido]
    );
    const getQualifDisabled = useCallback(
        (state) => !state?.ativa || (!state?.qualificatoria && limiteQualifAtingido),
        [limiteQualifAtingido]
    );

    const persistWorkflow = useCallback(
        (novo) => {
            changeModuleConfig({
                perguntasEssenciais: {
                    ...perguntasEssenciais,
                    workflow: novo,
                },
            });
        },
        [changeModuleConfig, perguntasEssenciais]
    );

    const persistPerguntasFixas = useCallback(
        (novas) => {
            changeModuleConfig({
                perguntasEssenciais: {
                    ...perguntasEssenciais,
                    perguntasFixas: novas,
                },
            });
        },
        [changeModuleConfig, perguntasEssenciais]
    );

    const persistModo = useCallback(
        (novoModo) => {
            if (novoModo === modo) return;
            changeModuleConfig({
                perguntasEssenciais: {
                    ...perguntasEssenciais,
                    modo: novoModo,
                },
            });
        },
        [changeModuleConfig, perguntasEssenciais, modo]
    );

    return (
        <TooltipProvider delayDuration={150}>
            <div className="flex flex-col gap-4">
                <Card className="rounded-xl border border-gray-100 bg-white">
                    <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50 shrink-0">
                                    <Iconify icon="mdi:sitemap-outline" width={20} className="text-violet-600" />
                                </div>
                                <div className="min-w-0">
                                    <CardTitle>Perguntas Essenciais</CardTitle>
                                    <CardDescription>
                                        Defina como a Lais coleta as informações do lead antes de buscar imóveis.
                                    </CardDescription>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 shrink-0">
                                <InfoBadge
                                    variant="violet"
                                    count={totalAtivas}
                                    limit={LIMITE_ATIVAS}
                                    label="essenciais"
                                    atingiu={limiteAtivasAtingido}
                                    tooltip="Essencial: A Lais coleta a informação durante a conversa, no melhor momento. Se o lead já responder espontaneamente, ela não pergunta de novo."
                                />
                                <InfoBadge
                                    variant="blue"
                                    count={totalQualif}
                                    limit={LIMITE_QUALIF}
                                    label="qualificatórias"
                                    atingiu={limiteQualifAtingido}
                                    tooltip="Qualificatória: Obrigatória para qualificar. O lead só é enviado ao CRM depois que essa pergunta for respondida. Só pode ser marcada em perguntas Essenciais."
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-5">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                                Modo de coleta
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <ModoCard
                                    active={modo === 'fixa'}
                                    onClick={() => persistModo('fixa')}
                                    icon="mdi:format-list-bulleted"
                                    title="Coleta fixa"
                                    description="Mesmas perguntas para todos os tipos de imóvel. Ideal para operações com fluxo padronizado."
                                />
                                <ModoCard
                                    active={modo === 'por_tipo'}
                                    onClick={() => persistModo('por_tipo')}
                                    icon="mdi:sitemap-outline"
                                    title="Coleta por tipo de imóvel"
                                    description="Personalize as perguntas para cada tipo (casa, apartamento, comercial, etc.)."
                                />
                            </div>
                        </div>

                        {modo === 'fixa' ? (
                            <PerguntasGlobaisCard
                                perguntas={perguntasFixas}
                                onChangePerguntas={persistPerguntasFixas}
                                getEssencialDisabled={getEssencialDisabled}
                                getQualifDisabled={getQualifDisabled}
                            />
                        ) : (
                            <PerguntasFlowCanvas
                                workflow={workflow}
                                onChange={persistWorkflow}
                                getEssencialDisabled={getEssencialDisabled}
                                getQualifDisabled={getQualifDisabled}
                            />
                        )}
                    </CardContent>
                </Card>
            </div>
        </TooltipProvider>
    );
}
